<template>
  <div class="task-details-item patient-tasks-section" data-test="patient-tasks-card">
    <div class="task-details-item-header">
      <h5 class="section-title">
        <i class="fa fa-check-square-o section-icon"></i>
        Tasks
        <span v-if="openTasks.length && !loadFailed" class="badge badge-pill badge-light count-badge">{{ openTasks.length }}</span>
      </h5>
    </div>
    <div class="section-content">
      <div v-if="!loaded" class="text-muted small"><i class="fa fa-spinner fa-spin mr-1"></i>Loading tasks…</div>
      <div v-else-if="loadFailed" class="text-muted no-tasks" data-test="patient-tasks-failed">Couldn't load tasks right now.</div>
      <div v-else-if="openTasks.length === 0" class="text-muted no-tasks" data-test="patient-tasks-empty">No open tasks for this patient.</div>
      <div v-else class="table-responsive">
        <table class="table table-hover patient-tasks-table">
          <thead><tr><th>Task</th><th>Due</th><th>Assigned to</th><th>Status</th></tr></thead>
          <tbody>
            <tr v-for="t in openTasks" :key="t.id" class="clickable" :data-task-id="t.id" @click="openDetails(t.id)">
              <td class="task-text">{{ previewText(t) }}</td>
              <td class="text-nowrap">{{ formatDue(t) }}</td>
              <td>{{ assigneeNames(t) }}</td>
              <td><span class="badge" :class="statusClass(t)">{{ statusLabel(t) }}</span></td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="completedTasks.length && !loadFailed" class="mt-2">
        <a href="#" class="small" data-test="patient-tasks-toggle-completed" @click.prevent="showCompleted = !showCompleted">
          {{ showCompleted ? 'Hide' : 'Show' }} {{ completedTasks.length }} completed
        </a>
        <div v-if="showCompleted" class="table-responsive mt-2">
          <table class="table table-hover patient-tasks-table completed">
            <tbody>
              <tr v-for="t in completedTasks" :key="t.id" class="clickable" :data-task-id="t.id" @click="openDetails(t.id)">
                <td class="task-text">{{ previewText(t) }}</td>
                <td class="text-nowrap">{{ formatDue(t) }}</td>
                <td>{{ assigneeNames(t) }}</td>
                <td><span class="badge badge-success">Completed</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment-timezone';
import { mapState, mapActions } from 'vuex';
import TodoDetails from '@/pages/TaskList/components/TodoDetails.vue';
import { stripMentions } from '@/utils/taskPatients';
import { dateFormatFor } from '@/utils/taskDraft';

const DUE_FORMAT = 'MM/DD/YYYY h:mm:ss a';

export default {
  name: 'PatientTasksCard',

  props: {
    siteId: { type: String, required: true },
    patNum: { type: [Number, String], required: true },
  },

  data() {
    return {
      loaded: false,
      // The listener errored (e.g. a denied read on a dead Firebase session, 2026-10-08) —
      // shown instead of "No open tasks", which would be a wrong answer.
      loadFailed: false,
      showCompleted: false,
      unsubscribe: null,
      // Bumped on every subscribe() so a stale (slow) dispatch resolution can tell
      // it has been superseded and must not store/keep its listener. See subscribe().
      subscribeToken: 0,
      wikiPages: [],
    };
  },

  computed: {
    ...mapState('tasks', { tasks: (s) => s.tasksTaggedWithPatient }),
    ...mapState('customer', ['employees', 'patients', 'settings']),

    numericPatNum() {
      return Number(this.patNum);
    },

    // The office date format (MM/DD/YYYY, or DD/MM/YYYY for Africa/Maseru), read
    // from GENERAL_TIMEZONE exactly as TaskComposer does.
    officeDateFormat() {
      const tz = _.keyBy(this.settings, 'Property')?.GENERAL_TIMEZONE?.PropertyValue;
      return dateFormatFor(tz);
    },

    // DEFENSIVE per-patient filter. `tasksTaggedWithPatient` is a SHARED Vuex slot:
    // PracticePulse's CheckoutNotes.vue writes the same slot and NEVER tears its
    // listener down, so a stale listener from earlier in the session can overwrite
    // it with ANOTHER patient's tasks. Keep only tasks actually tagged with THIS
    // patient; a task with no `taggedPatients` array cannot have come from this
    // query, so it is dropped.
    patientTasks() {
      const n = this.numericPatNum;
      return (this.tasks || []).filter(
        (t) => Array.isArray(t.taggedPatients) && t.taggedPatients.includes(n)
      );
    },

    openTasks() {
      const list = this.patientTasks.filter((t) => t.status !== 'COMPLETED');
      return list.slice().sort((a, b) => {
        const da = moment(a.dueAt, DUE_FORMAT);
        const db = moment(b.dueAt, DUE_FORMAT);
        const va = da.isValid();
        const vb = db.isValid();
        if (!va && !vb) return 0;
        if (!va) return 1; // invalid dates last
        if (!vb) return -1;
        return da.valueOf() - db.valueOf();
      });
    },

    completedTasks() {
      return this.patientTasks.filter((t) => t.status === 'COMPLETED');
    },
  },

  watch: {
    patNum() {
      this.subscribe();
    },
  },

  mounted() {
    this.subscribe();
  },

  beforeDestroy() {
    this.teardown();
  },

  methods: {
    ...mapActions('tasks', ['getTasksTaggedWithPatient', 'getTaskDetails']),
    ...mapActions('wiki', ['getWikiPages']),

    async subscribe() {
      this.teardown();
      // Invalidate any in-flight subscribe BEFORE the await, valid or not, so a
      // slow resolution knows it has been superseded.
      const token = ++this.subscribeToken;
      const n = this.numericPatNum;
      // `Number('')`, `Number(null)`, `Number('  ')` are all 0 and finite — a blank
      // or zero/negative patNum is not a real patient, so require n > 0.
      if (!Number.isFinite(n) || n <= 0 || !this.siteId) {
        this.loaded = true;
        return;
      }
      const unsub = await this.getTasksTaggedWithPatient({
        siteID: this.siteId,
        patientToLookFor: n,
        // Only the CURRENT subscription may flip the card to "couldn't load".
        onError: () => {
          if (token === this.subscribeToken) this.loadFailed = true;
        },
      });
      // A newer subscribe() started while we awaited — this resolution is stale.
      // Never store it or leave its listener live.
      if (token !== this.subscribeToken) {
        if (typeof unsub === 'function') {
          unsub();
          this.$store.commit('customer/removeFirestoreListener', unsub);
        }
        return;
      }
      this.unsubscribe = unsub;
      this.loaded = true;
    },

    teardown() {
      if (typeof this.unsubscribe === 'function') {
        this.unsubscribe();
        this.$store.commit('customer/removeFirestoreListener', this.unsubscribe);
      }
      this.unsubscribe = null;
      this.$store.commit('tasks/resetTasksTaggedWithPatient');
      this.loaded = false;
      this.loadFailed = false;
    },

    // Render the Due date from the PARSED dueAt in the office format — the raw
    // dueAtStr is stored inconsistently (workflow tasks write '2026-05-06',
    // composer tasks '05/06/2026'), so we normalize like My Tasks does. Fall back
    // to the raw string only when dueAt cannot be parsed.
    formatDue(t) {
      const d = moment(t.dueAt, DUE_FORMAT);
      return d.isValid() ? d.format(this.officeDateFormat) : (t.dueAtStr || '—');
    },

    previewText(t) {
      const s = stripMentions(t.content);
      return s.length > 140 ? s.slice(0, 140) + '…' : s;
    },

    assigneeNames(t) {
      const ids = Array.isArray(t.assignees) && t.assignees.length
        ? t.assignees
        : (t.assignedToEmpID ? [t.assignedToEmpID] : []);
      const names = ids
        .map((id) => {
          const emp = (this.employees || []).find((e) => e.EmployeeID === id);
          return emp ? emp.Name : null;
        })
        .filter(Boolean);
      return names.length ? names.join(', ') : '—';
    },

    isOverdue(t) {
      const d = moment(t.dueAt, DUE_FORMAT);
      return d.isValid() && d.isBefore(moment(), 'day');
    },

    statusLabel(t) {
      return this.isOverdue(t) ? 'Overdue' : 'Open';
    },

    statusClass(t) {
      return this.isOverdue(t) ? 'badge-danger' : 'badge-warning';
    },

    async openDetails(taskID) {
      try {
        // Lazy-load wiki pages once — TodoDetails wants them for its wiki picker.
        if (!this.wikiPages.length) {
          try {
            const r = await this.getWikiPages({ siteID: this.siteId });
            this.wikiPages = (r && r[0]) || [];
          } catch (e) {
            this.wikiPages = [];
          }
        }
        const task = await this.getTaskDetails({ taskID });
        if (!task) {
          this.$toast.error('Task not found or could not be loaded.');
          return;
        }
        this.$showPanel({
          component: TodoDetails,
          openOn: 'right',
          width: 750,
          props: {
            task,
            wikiPages: this.wikiPages,
            patients: this.patients,
            siteTags: this.$store.state.tasks.siteTaskTags || [],
          },
        });
      } catch (error) {
        console.log(`Error while opening task details: ${error}`);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// ─────────────────────────────────────────────────────────────────────────────
// ⛔ CARD CHROME BELOW IS A DELIBERATE MIRROR OF THE PARENT'S. See the full
// explanation at InsurancePanel.vue:558. Short version: PatientProfile.scss is
// SCOPED, so a child component's ROOT `.task-details-item` picks up the parent's
// card rules, but its DESCENDANTS (`.task-details-item-header`, `.section-title`,
// `.section-icon`, `.section-content`) match NOTHING unless re-declared here.
// Values copied verbatim from InsurancePanel.vue:584-616. Do NOT redefine the
// root `.task-details-item` — the parent's scoped rule already applies to it, and
// redefining it is the one thing that fights the parent.
// ─────────────────────────────────────────────────────────────────────────────
.patient-tasks-section {
  .task-details-item-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid #e2e8f0;
    background-color: #f8fafc;
    border-radius: 12px 12px 0 0;
    flex-wrap: wrap;
    gap: 12px;

    .section-title {
      margin: 0;
      font-size: 1rem;
      font-weight: 600;
      color: #0f172a;
      display: flex;
      align-items: center;

      .section-icon {
        margin-right: 0.75rem;
        color: #3abf94;
        font-size: 1.125rem;
        width: 20px;
        text-align: center;
      }
    }
  }

  .section-content {
    padding: 20px 24px;
  }

  // Table box — mirrors PatientProfile.scss:438-450 so this card's table reads like
  // the page's sibling cards (rounded, bordered, no double borders).
  .table-responsive {
    margin: 0;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e2e8f0;

    .table {
      margin-bottom: 0;
      border: none;
    }
  }

  .clickable {
    cursor: pointer;
  }

  .task-text {
    max-width: 320px;
  }

  .count-badge {
    font-size: 0.7rem;
    vertical-align: middle;
    margin-left: 0.5rem;
  }

  .patient-tasks-table td {
    vertical-align: middle;
    font-size: 0.875rem;
  }

  // Status badge — mirrors PatientProfile.scss:849-858 (the Follow-ups card).
  .patient-tasks-table .badge {
    font-size: 0.75rem;
    padding: 0.375rem 0.75rem;
    border-radius: 20px;
  }
}
</style>
